// Adapter wrapping the Drawflow instance.
// Deps are injected as lazy getters so the adapter can be created before
// state.drawflow is initialized (e.g. before initDrawflow() completes).
//
// getDrawflow()       — returns the live Drawflow instance or null
// getActiveModuleName() — returns state.activeFlowModuleName
// getFallbackData()   — returns state.project.flow?.drawflowData when drawflow is absent

export function createDrawflowAdapter({ getDrawflow, getActiveModuleName, getFallbackData }) {
    function getExportSnapshot() {
        const df = getDrawflow();
        return df?.export() || getFallbackData() || {};
    }

    function getModuleData(snapshot, moduleName) {
        return snapshot?.drawflow?.[moduleName]?.data || {};
    }

    function getActiveModuleData() {
        return getModuleData(getExportSnapshot(), getActiveModuleName());
    }

    function refreshConnections() {
        const df = getDrawflow();
        if (!df || typeof df.updateConnectionNodes !== "function") return;
        const moduleData = getActiveModuleData();
        for (const nodeId of Object.keys(moduleData)) {
            try { df.updateConnectionNodes("node-" + nodeId); } catch (e) { /* ignore stale node ids */ }
        }
    }

    function scheduleConnectionRefresh() {
        if (!getDrawflow()) return;
        const run = () => refreshConnections();
        requestAnimationFrame(() => { run(); requestAnimationFrame(run); });
        setTimeout(run, 0);
        setTimeout(run, 80);
        setTimeout(run, 220);
    }

    // importExport — clears the Drawflow instance and imports exportedData.
    // refreshNodeHtml(node) is an optional callback that returns new HTML for a node,
    // or null/undefined to leave the existing html unchanged.
    function importExport(exportedData, refreshNodeHtml) {
        const df = getDrawflow();
        if (!df) return;
        if (typeof refreshNodeHtml === "function") {
            for (const moduleName of Object.keys(exportedData.drawflow || {})) {
                const moduleData = getModuleData(exportedData, moduleName);
                for (const nodeId of Object.keys(moduleData)) {
                    const updated = refreshNodeHtml(moduleData[nodeId]);
                    if (updated != null) moduleData[nodeId].html = updated;
                }
            }
        }
        df.clear();
        df.import(exportedData);
    }

    // addConnection — thin wrapper around df.addConnection so callers don't hold a df reference.
    // Throws if Drawflow rejects the connection (caller handles alert/warning).
    function addConnection(sourceNodeId, targetNodeId, outputName, inputName) {
        const df = getDrawflow();
        if (!df) throw new Error("drawflow not initialized");
        df.addConnection(sourceNodeId, targetNodeId, outputName, inputName);
    }

    return {
        getExportSnapshot,
        getModuleData,
        getActiveModuleData,
        refreshConnections,
        scheduleConnectionRefresh,
        importExport,
        addConnection,
    };
}
